/**Usage Example**:
 * ```tsx
 * const updateButtonRef = useRef<UpdateButtonRef>(null);
 *
 * const handleSuccess = () => {
 *   updateButtonRef.current?.onSuccess();
 * };
 *
 * return <UpdateButton ref={updateButtonRef} isPending={isPending} />;
 * ```
 */

import { CheckIcon } from "@radix-ui/react-icons";
import { Loader } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import { trackEvent } from "~/utils/analytics";

const messages = defineMessages({
  update: { id: "settings.update", defaultMessage: "Update" },
  updating: { id: "settings.updating", defaultMessage: "Updating" },
  updated: { id: "settings.updated", defaultMessage: "Updated" },
});

type UpdateButtonProps = {
  isPending: boolean;
  disabled?: boolean;
};

export type UpdateButtonRef = {
  onSuccess: () => void;
};

const TIMEOUT = 2000;

export const UpdateButton = forwardRef<UpdateButtonRef, UpdateButtonProps>(
  ({ isPending, disabled = false }, ref) => {
    const intl = useIntl();
    const [isSuccessfullyUpdated, setIsSuccessfullyUpdated] = useState(false);
    const [btnText, setBtnText] = useState<keyof typeof messages>("update");

    useEffect(() => {
      if (isPending) {
        setBtnText("updating");
      } else if (isSuccessfullyUpdated) {
        setBtnText("updated");
      } else {
        setBtnText("update");
      }
    }, [isPending, isSuccessfullyUpdated]);

    useEffect(() => {
      if (isSuccessfullyUpdated) {
        const timer = setTimeout(() => {
          setIsSuccessfullyUpdated(false);
        }, TIMEOUT);

        return () => clearTimeout(timer);
      }
    }, [isSuccessfullyUpdated, TIMEOUT]);

    useImperativeHandle(ref, () => ({
      onSuccess() {
        setIsSuccessfullyUpdated(true);
      },
    }));

    return (
      <Button
        disabled={isPending || isSuccessfullyUpdated || disabled}
        className={`mt-4 self-start py-2 px-8 ${
          isSuccessfullyUpdated
            ? "bg-green-600 hover:bg-green-700"
            : "bg-blue-600 hover:bg-blue-700"
        }`}
        onClick={() => {
          trackEvent({
            eventName: "app/settings_account/update.button/click",
          });
        }}
        type="submit"
      >
        {isPending && <Loader className="mr-2 h-4 w-4 animate-spin-slow" />}
        {isSuccessfullyUpdated && <CheckIcon className="mr-2 h-4 w-4" />}
        {intl.formatMessage(messages[btnText])}
      </Button>
    );
  }
);

UpdateButton.displayName = "UpdateButton";
